import { useEffect, useState } from 'react';
import { UserState } from './user.store';
import { CheckoutState } from './checkout.store';
import { SearchState } from './search.store';
import { useAppSelector } from './hooks';

export function useCart() {
    return useAppSelector((state) => state.cart || {});
}

export function useCartSyncronized() {
    return useAppSelector((state) => state.cart.syncronized);
}

export function useCartProcessing(): boolean {
    return useAppSelector((state) => state.cart.processing && state.cart.processing.length > 0);
}

export function useCartCoupon(): string {
    return useAppSelector((state) => state.cart.coupon);
}

export function useCartCouponNewsletter(): string {
    return useAppSelector((state) => state.cart.couponNewsletter);
}

export function useCartQuantity(): number {
    return useAppSelector((state) => state.cart.products
            ? Object.values(state.cart.products)
                .reduce((acc: number, cur: number) => acc + cur, 0)
            : 0) as number;
}

export function useCartProductQuantity(id: number) {
    return useAppSelector((state) => state.cart.products[id] ? state.cart.products[id] : null);
}

export function useCartProductProcessing(productId: number): boolean {
    return useAppSelector((state) => {
        return state.cart.processing && state.cart.processing.filter((id: number) => id === productId).length > 0;
    });
}

export function useSearchPlace(): google.maps.places.PlaceResult {
    return useAppSelector((state) => state.search.place);
}

export function useSearchAddress(): string {
    const searchPlace = useSearchPlace();

    const components = searchPlace && searchPlace?.address_components ? searchPlace?.address_components : [];

    const street = [
        components.reduce((n, c) => c.types.includes('route') ? (c.short_name as string) : n, null),
        components.reduce((n, c) => c.types.includes('street_number') ? (c.long_name as string) : n, null),
    ].filter((c) => c).join(' ');

    const address = [
        components.reduce((n, c) => c.types.includes('locality') ? c.long_name : n, null),
        street,
    ].filter((c) => c).join(', ');

    return address;
}

export function useSearchStore(): SearchState {
    return useAppSelector((state) => state.search);
}

export function useStoredCheckoutData(): CheckoutState {
    return useAppSelector((state) => state.checkout);
}

export function useStoredUser(): UserState {
    return useAppSelector((state) => state.user);
}

export function useSearchedText(): string {
    const text = useAppSelector((state) => state.textSearch.text);
    return text;
}

export function useCartLoadFailed(): boolean {
    return useAppSelector((state) => state.cart.cartLoadFailed);
}

export function useExpiredNotices(): { wcId: number; name: string }[] {
    return useAppSelector((state) => state.cart.expiredNotices || []);
}

export function useCartExpiry(): { minMinutes: number; vendors: { name: string; minutes: number }[] } | null {
    const expiry = useAppSelector((state) => state.cart.vendorExpiry);
    const [now, setNow] = useState(new Date());

    useEffect(() => {
        const timer = setInterval(() => setNow(new Date()), 30000);
        return () => clearInterval(timer);
    }, []);

    if (!expiry || Object.keys(expiry).length === 0) return null;

    // Vendor hide times are in Europe/Sofia. This assumes the user's browser
    // is in the same timezone, which is true for 99%+ of users of a Bulgarian
    // food delivery app. For multi-country support, the backend should send
    // a UTC offset or ISO timestamp instead of a bare "HH:mm" string.
    const tz = 'Europe/Sofia';
    const vendors = Object.entries(expiry)
        .map(([, { name, defaultHide }]: [string, { name: string; defaultHide: string }]) => {
            const [h, m] = defaultHide.split(':').map(Number);
            const hideDate = new Date(now.toLocaleDateString('en-US', { timeZone: tz }));
            hideDate.setHours(h, m, 0, 0);
            const diffMs = hideDate.getTime() - now.getTime();
            return { name, minutes: Math.max(0, Math.floor(diffMs / 60000)) };
        })
        .filter(v => v.minutes > 0 && v.minutes <= 10)
        .sort((a, b) => a.minutes - b.minutes);

    if (vendors.length === 0) return null;
    return { minMinutes: vendors[0].minutes, vendors };
}
